import { Component, EventEmitter, Injector, Input, OnInit, Output } from '@angular/core';
import { MediaPictureOptions } from '../../model/MediaPictureOptions';
import { MediaPictureService } from '../../services/media-picture.service';
import { GtmService } from "../../services/gtm.service";
import { UpaService, UpaTarget } from "../../services/upa.service";
import { Module } from "../../model/ModuleModel";
import { AbstractComponent } from '../abstract.component';
import { ScreenType } from 'projects/common/model/ScreenType';
import { VideoPlayerService } from '../../services/video-player.service';
import { Video } from '../video-player/video.model';
import { ModuleService } from 'projects/common/services/module.service';
import { UtilsService } from 'projects/common/services/utils.service';

export interface PictureData {
  alt?: string;
  alt_mobile?: string;
  link?: string;
  video_playlist?: Video;
  src: string;
  src_mobile?: string;
  height?: number | null;
  width?: number | null;
  media_params?: { [parameter: string]: string };
  open_new_tab?: boolean;
  options?: { [key: string]: string};
}

@Component({
  selector: 'picture-component',
  template: `
    <picture [data]="module.data" [screenType]="screenType" (clickEvent)="trackGtm($event)" [moduleId]="getModuleId()" [moduleIndex]="module.row_number" [uniqueId]="module.unique_id" [position]="module.position"></picture>
  `,
  styleUrls: ['./picture.component.scss']
})
export class PictureComponent extends AbstractComponent {
  public static MODULE_ID: string = 'PictureModule';
  @Input() override module: Module<PictureData>;
  @Input() options: MediaPictureOptions;

  constructor(injector: Injector, private gtmService: GtmService) {
    super(injector);
  }

  trackGtm(event: any) {
    const href = event.currentTarget.href ? event.currentTarget.href : null;
    const hasVideo = event.currentTarget.className === 'picture-video';
    this.upaService.trackClickTopic({
      label: `image${hasVideo ? '-open-video' : ''}`,
      moduleId: this.module.id,
      uniqueId: this.module.unique_id,
      moduleIndex: this.module.row_number,
      positionIndex: this.module.position,
      target: UpaTarget.IMAGE,
      href: href
    });
    this.gtmService.trackEventIfApplicable("user-action", "picture-component", "Click", event.currentTarget.href ? event.currentTarget.href : null);
  }

  getModuleId(): string {
    return PictureComponent.MODULE_ID;
  }
}

@Component({
  selector: 'picture',
  template: `
    <div class="picture-container">
      <a *ngIf="hasLink()" [href]="data.link" (click)="clickHandler($event)" [attr.aria-label]="linkName" [attr.target]="data.open_new_tab ? '_blank' : null">
        <img class="picture" [src]="src" [alt]="data.alt || data.alt_mobile" [style]="style" loading="lazy"/>
      </a>
      <span *ngIf="hasVideo()" class="picture-video" (click)="clickHandler($event)">
        <img class="picture" [src]="src" [alt]="data.alt || data.alt_mobile" [style]="style" loading="lazy"/>
      </span>
      <img *ngIf="!hasLink() && !hasVideo()" class="picture" [src]="src + imageSize" [alt]="data.alt || data.alt_mobile" [style]="style"  loading="lazy"/>
    </div>
  `,
  styleUrls: ['./picture.component.scss']
})
export class Picture implements OnInit {
  static MODULE_ID: string = 'PictureModule';

  @Input() uniqueId?: string;
  @Input() moduleId: string;
  @Input() moduleIndex: number;
  @Input() position: number;
  @Input() data: PictureData;
  @Input() screenType: ScreenType;
  src?: string;
  style?: { [property: string]: string };

  desktopImageSize: string = '1280x140';
  mobileImageSize: string = '700x160';
  imageSize: string = ''

  constructor(private mediaPictureService: MediaPictureService,
    private videoPlayerService: VideoPlayerService,
    private moduleService: ModuleService,
    public utils: UtilsService,
    private upaService: UpaService,
    private gtmService: GtmService) {
  }

  ngOnInit(): void {
    this.src = this.getPictureSrc();
    this.style = this.getPictureStyle();

    if (this.moduleService.isRestrictResources(Picture.MODULE_ID)) {
      this.imageSize = '/' + (this.screenType == 'MOBILE' ? this.mobileImageSize : this.desktopImageSize);
    }
  }

  getPictureSrc(): string | undefined {
    let url = this.mediaPictureService.getMediaUrlByPictureData(this.data, this.screenType);
    if (url && this.data.media_params) {
      let params = '?' + Object.entries(this.data.media_params)
        .map(([key, value]) => `${key}=${value}`)
        .join('&')
      url = url.concat(params);
    }
    return url;
  }

  hasLink(): boolean {
    return this.data.link != null;
  }

  hasVideo(): boolean {
    return this.data.video_playlist !== undefined;
  }

  clickHandler(event: any) {
    if (this.data.video_playlist) {
      this.videoPlayerService.show({
        uniqueId: this.uniqueId,
        moduleId: this.moduleId,
        moduleIndex: this.moduleIndex,
        positionIndex: this.position,
      }, this.data.video_playlist);
    }
    this.trackGtm(event);
  }

  trackGtm(event: any) {
    const href = event.currentTarget.href ? event.currentTarget.href : null;
    this.upaService.trackClickTopic({
      label: 'image',
      moduleId: this.moduleId,
      moduleIndex: this.moduleIndex,
      positionIndex: this.position,
      target: UpaTarget.IMAGE,
      href: href,
      uniqueId: this.uniqueId,
    }, event);
    this.gtmService.trackEventIfApplicable("user-action" ,"picture-list-component", "Click", event.currentTarget.href? event.currentTarget.href: null);
  }

  getPictureStyle(): { [property: string]: string } {
    const style: { [property: string]: string } = {};
    if (this.data.height && this.data.width) {
      style['width'] = this.data.width + 'px';
      style['height'] = this.data.height + 'px';
    }
    return style;
  }

  get alt(): string {
    return this.screenType === ScreenType.MOBILE && this.data.alt_mobile ? this.data.alt_mobile : this.data.alt || '';
  }

  get linkName(): string {
    return this.utils.linkToName(this.alt);
  }

}

